import {useEffect, useState} from "react";
import {useDispatch} from "react-redux";
import {css} from "@panda/css";

import {Button} from "@pg-design/button-module";
import {MapMarkerLocationIcon} from "@pg-design/icons-module";
import {Image} from "@pg-design/image-module";
import {Loader} from "@pg-design/loader-module";
import {type IModalOverlayVariant, Modal} from "@pg-design/modal-module";
import {Highlight, Text} from "@pg-design/text-module";
import {getPhoneDataFromString, priceFormat, priceM2Format} from "@pg-mono/string-utils";
import {OFFER_TEST_IDS} from "@pg-mono/test-ids";

import {TurnstileAction} from "../../../app/constants/turnstile_actions";
import {createRpLeadPerformanceDebugTrace} from "../../../app/performance_debug";
import {prefetchTurnstileToken} from "../../../app/turnstile";
import {useSendMainLead} from "../../../application/hooks/use_send_main_lead";
import {setMainLeadData} from "../../../application/state/main_lead_slice";
import {getAmplitudeCtaLeadFormTrackingContext} from "../../../application/tracking/amplitude/get_amplitude_cta_lead_form_tracking_context";
import {useGetPropertyDetailQuery} from "../../../property/api/get_property_detail";
import {useAppSelector} from "../../../store/hooks";
import {getUserSourceDisplay} from "../../../tracking/tracking_utils/user_source";
import {useGetUserProfileQuery} from "../../../user/api/get_user_profile";
import {useGetHomeMatchConfigurationQuery} from "../../../user/api/patch_home_match_configuration";
import {HomeMatchBasicPreferencesFlowSource} from "../../../user/types/HomeMatchBasicPreferencesFlowTypes";
import {gtmLeadAfterRegistration} from "../../tracking/lead_after_registration_tracking";
import {PropertyInsightBox} from "./PropertyInsightBox";
import {useInsightVariant} from "./useInsightVariant";

interface IProps {
    isOpen: boolean;
    onConfirm: (isHomeMatchOn: boolean, source: HomeMatchBasicPreferencesFlowSource, sentApplicationUuid?: string | null) => void;
    onClose: () => void;
    onShowSimilar: () => void;
    overlayVariant?: IModalOverlayVariant;
}

export const LeadAfterRegistration = ({isOpen, onConfirm, onClose, onShowSimilar, overlayVariant}: IProps) => {
    const dispatch = useDispatch();

    const flowState = useAppSelector((state) => state.registrationFlow);
    const pageProperty = useAppSelector((state) => state.property.property);
    const isPageProperty = Boolean(pageProperty && pageProperty.id === flowState.propertyId);
    const {data: fetchedPropertyData, isLoading: isPropertyLoading} = useGetPropertyDetailQuery(flowState.propertyId as number, {
        skip: !isOpen || !flowState.propertyId || isPageProperty
    });
    const propertyData = isPageProperty ? pageProperty : fetchedPropertyData;
    const isLoading = !isPageProperty && isPropertyLoading;
    const {variant, buttonText: confirmButtonText, isLoading: isInsightLoading, percentageBelow, promotionExample} = useInsightVariant(propertyData);
    const [isSending, setIsSending] = useState(false);
    const {data: userProfile} = useGetUserProfileQuery();
    const {data: homeMatchConfig} = useGetHomeMatchConfigurationQuery();
    const sendMainLead = useSendMainLead();
    const gtmCategory = flowState.gtmSourceSection ?? (flowState.userSource !== undefined ? getUserSourceDisplay(flowState.userSource) : "");

    useEffect(() => {
        if (isOpen) {
            gtmLeadAfterRegistration("view", gtmCategory);
            prefetchTurnstileToken(TurnstileAction.APPLICATION_CREATE);
        }
    }, [isOpen]);

    useEffect(() => {
        if (flowState?.vendorId && flowState?.offerId && flowState?.propertyId && flowState.leadSource && flowState.leadSourceSection) {
            dispatch(
                setMainLeadData({
                    vendorId: flowState.vendorId,
                    offerId: flowState.offerId,
                    propertyId: flowState.propertyId,
                    source: flowState.leadSource,
                    sourceSection: flowState.leadSourceSection,
                    trackingMeta: {openButtonSourceText: flowState.gtmClickText ?? null, openButtonSource: flowState.gtmSourceSection ?? null}
                })
            );
        }
    }, [
        flowState.vendorId,
        flowState.offerId,
        flowState.propertyId,
        flowState.leadSource,
        flowState.leadSourceSection,
        flowState.gtmClickText,
        flowState.gtmSourceSection
    ]);

    const handleConfirm = async () => {
        if (!userProfile) {
            return;
        }

        const trace = createRpLeadPerformanceDebugTrace("rp_lead_after_registration_confirm", {
            homeMatchActive: Boolean(homeMatchConfig?.is_active),
            offerId: flowState.offerId,
            propertyId: flowState.propertyId,
            vendorId: flowState.vendorId
        });

        setIsSending(true);
        trace.mark("submit_loader:enabled");
        gtmLeadAfterRegistration("click", gtmCategory, confirmButtonText);
        // This modal sends the lead directly (no lead modal open), so the amplitude context is not in
        // the mainLead store - build it here or the generate_lead event gate in sendLead stays closed
        const amplitudeLeadFormEventTracking =
            flowState.leadSource != null && flowState.leadSourceSection != null
                ? getAmplitudeCtaLeadFormTrackingContext({
                      source: flowState.leadSource,
                      sourceSection: flowState.leadSourceSection,
                      leadData: undefined,
                      ctaText: confirmButtonText,
                      offerId: flowState.offerId,
                      propertyId: flowState.propertyId
                  })
                : null;
        try {
            const application = await sendMainLead(
                {
                    name: userProfile.name ?? "",
                    email: userProfile.email ?? "",
                    phone: getPhoneDataFromString(userProfile.phone ?? ""),
                    marketing_email: userProfile.email_marketing_consent ?? false,
                    marketing_sms: userProfile.phone_marketing_consent ?? false
                },
                undefined,
                amplitudeLeadFormEventTracking ? {analyticsParams: {amplitudeLeadFormEventTracking}} : undefined
            );

            onConfirm(homeMatchConfig?.is_active ?? false, "afterLead", application?.uuid ?? null);
        } finally {
            setIsSending(false);
            trace.end("submit_loader:disabled");
        }
    };

    const handleShowSimilar = () => {
        gtmLeadAfterRegistration("click", gtmCategory, "Pokaż podobne oferty");
        onShowSimilar();
    };

    const handleClose = () => {
        gtmLeadAfterRegistration("close", gtmCategory);
        onClose();
    };

    const handleKeepBrowsing = () => {
        gtmLeadAfterRegistration("click", gtmCategory, "Tylko się rozglądam");
        onClose();
    };

    const isDisabled = !propertyData || isLoading || isSending || !userProfile;

    return (
        <Modal onModalClose={handleClose} isOpen={isOpen} shouldCloseOnOverlayClick={false} shouldCloseOnEsc={false} overlayVariant={overlayVariant}>
            <Modal.Layout.EmptyResponsive closeButtonVariant="none" className={modalStyle} dataTestId={OFFER_TEST_IDS.LEAD_FORM.PROPERTY_PRICE_REVEALED_MODAL}>
                <Text variant="headline_3" as="p" mb={1}>
                    <Highlight>Cena jest już widoczna!</Highlight>
                </Text>
                <Text variant="body_copy_2">
                    Utworzyliśmy dla Ciebie darmowe konto. Od teraz masz <strong>pełny dostęp do wszystkich cen na portalu.</strong>
                </Text>

                <div className={propertyBoxStyle}>
                    {isLoading && (
                        <div className={loaderWrapperStyle}>
                            <Loader />
                        </div>
                    )}
                    {propertyData && (
                        <>
                            <Image src={propertyData.offer.main_image.m_img_440x248} alt="" width="496px" height="240px" />
                            <div className={propertyInfoStyle}>
                                {propertyData.price && propertyData.price_m2 && (
                                    <div className={priceRowStyle}>
                                        <Text variant="headline_3" as="p">
                                            <Highlight>
                                                <strong>{priceFormat(propertyData.price)}</strong>
                                            </Highlight>
                                        </Text>
                                        <Text variant="info_txt_1">
                                            <strong>{priceM2Format(propertyData.price_m2)}</strong>
                                        </Text>
                                    </div>
                                )}
                                <div className={regionRowStyle}>
                                    <MapMarkerLocationIcon size="1.2" wrapperSize="1.2" wrapperColor="transparent" />
                                    <Text variant="info_txt_1">{propertyData.offer.region.short_name}</Text>
                                </div>
                                <PropertyInsightBox
                                    variant={variant}
                                    isLoading={isInsightLoading}
                                    percentageBelow={percentageBelow}
                                    promotionExample={promotionExample}
                                />
                            </div>
                        </>
                    )}
                </div>
                <div className={buttonsWrapper}>
                    <Button
                        variant="filled_primary"
                        fullWidth
                        disabled={isDisabled}
                        isLoading={isSending}
                        onClick={handleConfirm}
                        className={buttonStyle}
                        dataTestId={OFFER_TEST_IDS.LEAD_FORM.CONFIRM_LEAD_BUTTON}
                    >
                        {confirmButtonText}
                    </Button>
                    <Button variant="outlined_secondary" fullWidth disabled={isDisabled} onClick={handleShowSimilar} className={buttonStyle}>
                        Zobacz podobne w tej okolicy
                    </Button>
                    <Button variant="none_secondary" size="x-small" fullWidth onClick={handleKeepBrowsing} className={css({minH: "40px"})}>
                        Tylko się rozglądam
                    </Button>
                </div>
            </Modal.Layout.EmptyResponsive>
        </Modal>
    );
};

const modalStyle = css({
    position: "relative",
    flexDirection: "column",
    pt: 1.5,
    px: 1.5,
    pb: 0,

    md: {
        pt: 3,
        px: 3,
        maxWidth: "544px"
    }
});

const propertyBoxStyle = css({
    borderRadius: 2,
    boxShadow: "2",
    my: 3,
    w: "100%",
    maxWidth: "496px",
    mx: "auto",

    "& img": {
        maxWidth: "100%"
    }
});

const loaderWrapperStyle = css({
    alignItems: "center",
    display: "flex",
    justifyContent: "center",
    p: 3
});

const propertyInfoStyle = css({
    p: 2
});

const priceRowStyle = css({
    alignItems: "baseline",
    display: "flex",
    gap: 1.5
});

const regionRowStyle = css({
    alignItems: "center",
    display: "flex",
    gap: 1.5,
    mb: 1.5
});

const buttonsWrapper = css({
    w: "100%",
    position: "sticky",
    bottom: 0,
    left: 0,
    backgroundColor: "var(--colors-white)",
    p: 1.5,
    md: {
        px: 3
    }
});

const buttonStyle = css({
    mb: 2,
    minH: "56px"
});
import {useEffect, useState} from "react";
import {useDispatch} from "react-redux";
import {css} from "@panda/css";

import {Button} from "@pg-design/button-module";
import {MapMarkerLocationIcon} from "@pg-design/icons-module";
import {Image} from "@pg-design/image-module";
import {Loader} from "@pg-design/loader-module";
import {type IModalOverlayVariant, Modal} from "@pg-design/modal-module";
import {Highlight, Text} from "@pg-design/text-module";
import {getPhoneDataFromString, priceFormat, priceM2Format} from "@pg-mono/string-utils";
import {OFFER_TEST_IDS} from "@pg-mono/test-ids";

import {TurnstileAction} from "../../../app/constants/turnstile_actions";
import {createRpLeadPerformanceDebugTrace} from "../../../app/performance_debug";
import {prefetchTurnstileToken} from "../../../app/turnstile";
import {useSendMainLead} from "../../../application/hooks/use_send_main_lead";
import {setMainLeadData} from "../../../application/state/main_lead_slice";
import {getAmplitudeCtaLeadFormTrackingContext} from "../../../application/tracking/amplitude/get_amplitude_cta_lead_form_tracking_context";
import {useGetPropertyDetailQuery} from "../../../property/api/get_property_detail";
import {useAppSelector} from "../../../store/hooks";
import {getUserSourceDisplay} from "../../../tracking/tracking_utils/user_source";
import {useGetUserProfileQuery} from "../../../user/api/get_user_profile";
import {useGetHomeMatchConfigurationQuery} from "../../../user/api/patch_home_match_configuration";
import {HomeMatchBasicPreferencesFlowSource} from "../../../user/types/HomeMatchBasicPreferencesFlowTypes";
import {gtmLeadAfterRegistration} from "../../tracking/lead_after_registration_tracking";
import {PropertyInsightBox} from "./PropertyInsightBox";
import {useInsightVariant} from "./useInsightVariant";

interface IProps {
    isOpen: boolean;
    onConfirm: (isHomeMatchOn: boolean, source: HomeMatchBasicPreferencesFlowSource, sentApplicationUuid?: string | null) => void;
    onClose: () => void;
    onShowSimilar: () => void;
    overlayVariant?: IModalOverlayVariant;
}

export const LeadAfterRegistration = ({isOpen, onConfirm, onClose, onShowSimilar, overlayVariant}: IProps) => {
    const dispatch = useDispatch();

    const flowState = useAppSelector((state) => state.registrationFlow);
    const pageProperty = useAppSelector((state) => state.property.property);
    const isPageProperty = Boolean(pageProperty && pageProperty.id === flowState.propertyId);
    const {data: fetchedPropertyData, isLoading: isPropertyLoading} = useGetPropertyDetailQuery(flowState.propertyId as number, {
        skip: !isOpen || !flowState.propertyId || isPageProperty
    });
    const propertyData = isPageProperty ? pageProperty : fetchedPropertyData;
    const isLoading = !isPageProperty && isPropertyLoading;
    const {variant, buttonText: confirmButtonText, isLoading: isInsightLoading, percentageBelow, promotionExample} = useInsightVariant(propertyData);
    const [isSending, setIsSending] = useState(false);
    const {data: userProfile} = useGetUserProfileQuery();
    const {data: homeMatchConfig} = useGetHomeMatchConfigurationQuery();
    const sendMainLead = useSendMainLead();
    const gtmCategory = flowState.gtmSourceSection ?? (flowState.userSource !== undefined ? getUserSourceDisplay(flowState.userSource) : "");

    useEffect(() => {
        if (isOpen) {
            gtmLeadAfterRegistration("view", gtmCategory);
            prefetchTurnstileToken(TurnstileAction.APPLICATION_CREATE);
        }
    }, [isOpen]);

    useEffect(() => {
        if (flowState?.vendorId && flowState?.offerId && flowState?.propertyId && flowState.leadSource && flowState.leadSourceSection) {
            dispatch(
                setMainLeadData({
                    vendorId: flowState.vendorId,
                    offerId: flowState.offerId,
                    propertyId: flowState.propertyId,
                    source: flowState.leadSource,
                    sourceSection: flowState.leadSourceSection,
                    trackingMeta: {openButtonSourceText: flowState.gtmClickText ?? null, openButtonSource: flowState.gtmSourceSection ?? null}
                })
            );
        }
    }, [
        flowState.vendorId,
        flowState.offerId,
        flowState.propertyId,
        flowState.leadSource,
        flowState.leadSourceSection,
        flowState.gtmClickText,
        flowState.gtmSourceSection
    ]);

    const handleConfirm = async () => {
        if (!userProfile) {
            return;
        }

        const trace = createRpLeadPerformanceDebugTrace("rp_lead_after_registration_confirm", {
            homeMatchActive: Boolean(homeMatchConfig?.is_active),
            offerId: flowState.offerId,
            propertyId: flowState.propertyId,
            vendorId: flowState.vendorId
        });

        setIsSending(true);
        trace.mark("submit_loader:enabled");
        gtmLeadAfterRegistration("click", gtmCategory, confirmButtonText);
        // This modal sends the lead directly (no lead modal open), so the amplitude context is not in
        // the mainLead store - build it here or the generate_lead event gate in sendLead stays closed
        const amplitudeLeadFormEventTracking =
            flowState.leadSource != null && flowState.leadSourceSection != null
                ? getAmplitudeCtaLeadFormTrackingContext({
                      source: flowState.leadSource,
                      sourceSection: flowState.leadSourceSection,
                      leadData: undefined,
                      ctaText: confirmButtonText,
                      offerId: flowState.offerId,
                      propertyId: flowState.propertyId
                  })
                : null;
        try {
            const application = await sendMainLead(
                {
                    name: userProfile.name ?? "",
                    email: userProfile.email ?? "",
                    phone: getPhoneDataFromString(userProfile.phone ?? ""),
                    marketing_email: userProfile.email_marketing_consent ?? false,
                    marketing_sms: userProfile.phone_marketing_consent ?? false
                },
                undefined,
                amplitudeLeadFormEventTracking ? {analyticsParams: {amplitudeLeadFormEventTracking}} : undefined
            );

            onConfirm(homeMatchConfig?.is_active ?? false, "afterLead", application?.uuid ?? null);
        } finally {
            setIsSending(false);
            trace.end("submit_loader:disabled");
        }
    };

    const handleShowSimilar = () => {
        gtmLeadAfterRegistration("click", gtmCategory, "Pokaż podobne oferty");
        onShowSimilar();
    };

    const handleClose = () => {
        gtmLeadAfterRegistration("close", gtmCategory);
        onClose();
    };

    const handleKeepBrowsing = () => {
        gtmLeadAfterRegistration("click", gtmCategory, "Tylko się rozglądam");
        onClose();
    };

    const isDisabled = !propertyData || isLoading || isSending || !userProfile;

    return (
        <Modal onModalClose={handleClose} isOpen={isOpen} shouldCloseOnOverlayClick={false} shouldCloseOnEsc={false} overlayVariant={overlayVariant}>
            <Modal.Layout.EmptyResponsive closeButtonVariant="none" className={modalStyle} dataTestId={OFFER_TEST_IDS.LEAD_FORM.PROPERTY_PRICE_REVEALED_MODAL}>
                <Text variant="headline_3" as="p" mb={1}>
                    <Highlight>Cena jest już widoczna!</Highlight>
                </Text>
                <Text variant="body_copy_2">
                    Utworzyliśmy dla Ciebie darmowe konto. Od teraz masz <strong>pełny dostęp do wszystkich cen na portalu.</strong>
                </Text>

                <div className={propertyBoxStyle}>
                    {isLoading && (
                        <div className={loaderWrapperStyle}>
                            <Loader />
                        </div>
                    )}
                    {propertyData && (
                        <>
                            <Image src={propertyData.offer.main_image.m_img_440x248} alt="" width="496px" height="240px" />
                            <div className={propertyInfoStyle}>
                                {propertyData.price && propertyData.price_m2 && (
                                    <div className={priceRowStyle}>
                                        <Text variant="headline_3" as="p">
                                            <Highlight>
                                                <strong>{priceFormat(propertyData.price)}</strong>
                                            </Highlight>
                                        </Text>
                                        <Text variant="info_txt_1">
                                            <strong>{priceM2Format(propertyData.price_m2)}</strong>
                                        </Text>
                                    </div>
                                )}
                                <div className={regionRowStyle}>
                                    <MapMarkerLocationIcon size="1.2" wrapperSize="1.2" wrapperColor="transparent" />
                                    <Text variant="info_txt_1">{propertyData.offer.region.short_name}</Text>
                                </div>
                                <PropertyInsightBox
                                    variant={variant}
                                    isLoading={isInsightLoading}
                                    percentageBelow={percentageBelow}
                                    promotionExample={promotionExample}
                                />
                            </div>
                        </>
                    )}
                </div>
                <div className={buttonsWrapper}>
                    <Button
                        variant="filled_primary"
                        fullWidth
                        disabled={isDisabled}
                        isLoading={isSending}
                        onClick={handleConfirm}
                        className={buttonStyle}
                        dataTestId={OFFER_TEST_IDS.LEAD_FORM.CONFIRM_LEAD_BUTTON}
                    >
                        {confirmButtonText}
                    </Button>
                    <Button variant="outlined_secondary" fullWidth disabled={isDisabled} onClick={handleShowSimilar} className={buttonStyle}>
                        Zobacz podobne w tej okolicy
                    </Button>
                    <Button variant="none_secondary" size="x-small" fullWidth onClick={handleKeepBrowsing} className={css({minH: "40px"})}>
                        Tylko się rozglądam
                    </Button>
                </div>
            </Modal.Layout.EmptyResponsive>
        </Modal>
    );
};

const modalStyle = css({
    position: "relative",
    flexDirection: "column",
    pt: 1.5,
    px: 1.5,
    pb: 0,

    md: {
        pt: 3,
        px: 3,
        maxWidth: "544px"
    }
});

const propertyBoxStyle = css({
    borderRadius: 2,
    boxShadow: "2",
    my: 3,
    w: "100%",
    maxWidth: "496px",
    mx: "auto",

    "& img": {
        maxWidth: "100%"
    }
});

const loaderWrapperStyle = css({
    alignItems: "center",
    display: "flex",
    justifyContent: "center",
    p: 3
});

const propertyInfoStyle = css({
    p: 2
});

const priceRowStyle = css({
    alignItems: "baseline",
    display: "flex",
    gap: 1.5
});

const regionRowStyle = css({
    alignItems: "center",
    display: "flex",
    gap: 1.5,
    mb: 1.5
});

const buttonsWrapper = css({
    w: "100%",
    position: "sticky",
    bottom: 0,
    left: 0,
    backgroundColor: "var(--colors-white)",
    p: 1.5,
    md: {
        px: 3
    }
});

const buttonStyle = css({
    mb: 2,
    minH: "56px"
});
